import {
  useAbsatzPlanWertCreateOrUpdate,
  useAbsatzUmsatzPlanWertCreateOrUpdate,
  useUmsatzPlanWertCreateOrUpdate,
} from '@/domain/controlling/hooks';
import {
  CreateOrUpdateAbsatzPlanWertAndInterneNotizMutation,
  CreateOrUpdateAbsatzUmsatzPlanWertAndInterneNotizMutation,
  CreateOrUpdateUmsatzPlanWertAndInterneNotizMutation,
  Currency,
} from '@/gql/graphql';
import { maskitoParseNumber } from '@maskito/kit';
import { MouseEvent } from 'react';
import { useRouteParams } from './useRouteParams';
import { useTagebuchEinstellungen } from '@/domain/controlling/hooks/useTagebuchEinstellungen';
import { useDialogContext } from './DialogContext';
import { FetchResult } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useStandortContext } from '@/features/standortContext/StandortContext';

export type MutationResult =
  | FetchResult<CreateOrUpdateAbsatzPlanWertAndInterneNotizMutation>
  | FetchResult<CreateOrUpdateUmsatzPlanWertAndInterneNotizMutation>
  | FetchResult<CreateOrUpdateAbsatzUmsatzPlanWertAndInterneNotizMutation>;

// eslint-disable-next-line max-lines-per-function
export function useFormSubmit(
  onSuccess: (result: MutationResult) => void,
  displayError: (message?: string) => void,
) {
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { searchParams } = useRouteParams();
  const { umsatzplanungAnzeige } = useTagebuchEinstellungen(standortId);
  const { planwerteForm, dispatch } = useDialogContext();
  const { formValues } = planwerteForm;
  const { t } = useTranslation();

  const [createOrUpdateAbsatzPlan] = useAbsatzPlanWertCreateOrUpdate();
  const [createOrUpdateUmsatzPlan] = useUmsatzPlanWertCreateOrUpdate();
  const [createOrUpdateAbsatzUmsatzPlan] =
    useAbsatzUmsatzPlanWertCreateOrUpdate();

  const handleUpdateResult = (result: MutationResult) => {
    const hasInterneNotizError =
      result.data.createOrUpdateInterneNotiz.__typename ===
        'UmsatzplanungUnkownError' ||
      result.data.createOrUpdateInterneNotiz.__typename ===
        'TagesInformationenInterneNotizUpdateError';

    if (hasInterneNotizError) {
      displayError(
        t(
          'domain.controlling.umsatzplanung.planung.errors.updateTagesInformationenError',
        ),
      );
      return false;
    }
    return true;
  };

  function handleUpdateUmsatzPlanSubmit(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    dispatch({ type: 'submit' });

    createOrUpdateUmsatzPlan({
      createOrUpdateInterneNotizInput: {
        datum: searchParams.datum,
        interneNotiz: formValues.interneNotiz,
        standortId,
      },
      createOrUpdateUmsatzPlanInput: {
        standortId,
        datum: searchParams.datum,
        plan: formValues.umsatzPlan
          ? {
              amount: maskitoParseNumber(formValues.umsatzPlan, ','),
              currency: Currency.Eur,
            }
          : null,
        produktivitaet: formValues.umsatzProduktivitaet
          ? {
              amount: maskitoParseNumber(formValues.umsatzProduktivitaet, ','),
              currency: Currency.Eur,
            }
          : null,
      },
    })
      .then((result) => {
        if (!handleUpdateResult(result)) return;

        const hasUmsatzError =
          result.data.createOrUpdateUmsatzPlan.__typename ===
            'UmsatzUpdateResultUpdateError' ||
          result.data.createOrUpdateUmsatzPlan.__typename ===
            'UmsatzplanungUnkownError';

        if (hasUmsatzError) {
          displayError(
            t(
              'domain.controlling.umsatzplanung.planung.errors.updateResultUpdateError',
            ),
          );
          return;
        }

        onSuccess(result);
      })
      .catch((error) => displayError(error));
  }

  function handleUpdateAbsatzPlanSubmit(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    dispatch({ type: 'submit' });

    createOrUpdateAbsatzPlan({
      createOrUpdateInterneNotizInput: {
        datum: searchParams.datum,
        interneNotiz: formValues.interneNotiz,
        standortId,
      },
      createOrUpdateAbsatzPlanInput: {
        standortId,
        datum: searchParams.datum,
        plan: formValues.absatzPlan
          ? maskitoParseNumber(formValues.absatzPlan, ',')
          : null,
        produktivitaet: formValues.absatzProduktivitaet
          ? maskitoParseNumber(formValues.absatzProduktivitaet, ',')
          : null,
      },
    })
      .then((result) => {
        if (!handleUpdateResult(result)) return;

        const hasAbsatzError =
          result.data.createOrUpdateAbsatzPlan.__typename ===
            'AbsatzUpdateResultUpdateError' ||
          result.data.createOrUpdateAbsatzPlan.__typename ===
            'UmsatzplanungUnkownError';

        if (hasAbsatzError) {
          displayError(
            t(
              'domain.controlling.umsatzplanung.planung.errors.updateResultUpdateError',
            ),
          );
          return;
        }

        onSuccess(result);
      })
      .catch((error) => displayError(error));
  }

  // eslint-disable-next-line max-lines-per-function
  function handleUpdateAbsatzUmsatzPlanSubmit(
    event: MouseEvent<HTMLButtonElement>,
  ) {
    event.preventDefault();
    dispatch({ type: 'submit' });

    createOrUpdateAbsatzUmsatzPlan({
      createOrUpdateInterneNotizInput: {
        datum: searchParams.datum,
        interneNotiz: formValues.interneNotiz,
        standortId,
      },
      createOrUpdateUmsatzPlanInput: {
        standortId,
        datum: searchParams.datum,
        plan: formValues.umsatzPlan
          ? {
              amount: maskitoParseNumber(formValues.umsatzPlan, ','),
              currency: Currency.Eur,
            }
          : null,
        produktivitaet: formValues.umsatzProduktivitaet
          ? {
              amount: maskitoParseNumber(formValues.umsatzProduktivitaet, ','),
              currency: Currency.Eur,
            }
          : null,
      },
      createOrUpdateAbsatzPlanInput: {
        standortId,
        datum: searchParams.datum,
        plan: formValues.absatzPlan
          ? maskitoParseNumber(formValues.absatzPlan, ',')
          : null,
        produktivitaet: formValues.absatzProduktivitaet
          ? maskitoParseNumber(formValues.absatzProduktivitaet, ',')
          : null,
      },
    })
      .then((result) => {
        if (!handleUpdateResult(result)) return;

        const hasAbsatzOrUmsatzError =
          result.data.createOrUpdateAbsatzPlan.__typename ===
            'AbsatzUpdateResultUpdateError' ||
          result.data.createOrUpdateAbsatzPlan.__typename ===
            'UmsatzplanungUnkownError' ||
          result.data.createOrUpdateUmsatzPlan.__typename ===
            'UmsatzUpdateResultUpdateError' ||
          result.data.createOrUpdateUmsatzPlan.__typename ===
            'UmsatzplanungUnkownError';

        if (hasAbsatzOrUmsatzError) {
          displayError(
            t(
              'domain.controlling.umsatzplanung.planung.errors.updateResultUpdateError',
            ),
          );
          return;
        }

        onSuccess(result);
      })
      .catch((error) => {
        Sentry.captureException(error);
        displayError();
      });
  }

  if (umsatzplanungAnzeige === 'ABSATZ_PRODUKTIVITAET') {
    return { handleSubmit: handleUpdateAbsatzPlanSubmit };
  } else if (umsatzplanungAnzeige === 'UMSATZ_ABSATZ_PRODUKTIVITAET') {
    return { handleSubmit: handleUpdateAbsatzUmsatzPlanSubmit };
  }
  return { handleSubmit: handleUpdateUmsatzPlanSubmit };
}
